import { Link } from 'react-router-dom';
import {
  FiEye, FiTarget, FiStar, FiAward, FiTrendingUp, FiShield,
  FiArrowRight, FiMessageCircle,
} from 'react-icons/fi';
import { BsLightningCharge, BsPeopleFill } from 'react-icons/bs';
import { MdOutlineVerifiedUser } from 'react-icons/md';

const values = [
  { Icon: FiAward,              label: 'Professionalism' },
  { Icon: FiShield,             label: 'Integrity' },
  { Icon: FiStar,               label: 'Reliability' },
  { Icon: BsLightningCharge,    label: 'Innovation' },
  { Icon: FiTarget,             label: 'Customer Focus' },
];

const team = [
  { name: 'Erick Alex Kissitu', role: 'Managing Director',   initials: 'EK' },
  { name: 'Sarah Mwakajinga',   role: 'Accountant Executive', initials: 'SM' },
  { name: 'Rehema Ridhiwan',    role: 'HR & Administration',  initials: 'RR' },
  { name: 'Eivod Tizulwa',      role: 'Projects Supervisor',  initials: 'ET' },
];

const milestones = [
  { year: '2015', event: 'KIKI SECURITY (T) LTD founded in Morogoro as the main branch.' },
  { year: '2017', event: 'Expanded services to include CCTV installation and electric fence systems.' },
  { year: '2019', event: 'Opened Dar es Salaam branch office to extend regional coverage.' },
  { year: '2021', event: 'Launched manpower and customer service agent division.' },
  { year: '2025', event: 'Serving 500+ clients across Tanzania with 1,000+ deployed personnel.' },
];

export default function About() {
  return (
    <div className="animate-fadeUp">

      {/* ── HERO ── */}
      <section className="relative pt-40 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-navy-DEFAULT via-navy-mid to-navy-light" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(201,168,76,0.10),transparent_60%)]" />
        <div className="relative max-w-7xl mx-auto px-6">
          <span className="section-tag">About Us</span>
          <h1 className="section-title mt-2 mb-4">
            Who We <span className="text-gold">Are</span>
          </h1>
          <div className="gold-divider" />
          <p className="text-gray-300 text-lg max-w-2xl leading-relaxed">
            Founded in 2015, KIKI SECURITY (T) LTD provides end-to-end protection and workforce support
            for corporate, residential, industrial, and public-sector clients across Tanzania.
          </p>
        </div>
        <div className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-t from-navy-DEFAULT to-transparent" />
      </section>

      {/* ── INTRO ── */}
      <section className="py-20 max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="section-tag">Our Story</span>
            <h2 className="section-title mb-4">
              A Decade of <span className="text-gold">Trusted Service</span>
            </h2>
            <div className="gold-divider" />
            <p className="text-gray-400 leading-relaxed mb-4">
              Our team blends disciplined security professionals, certified technicians, and trained service agents
              to deliver dependable results — on time and on budget.
            </p>
            <p className="text-gray-400 leading-relaxed mb-4">
              We operate with integrity, continuous improvement, and customer centricity. From guarding deployments
              to technology installations and short-term staffing, we design solutions that align with each client's
              risk profile, operations, and budget.
            </p>
            <p className="text-gray-400 leading-relaxed">
              Fully licensed and registered, we adhere to Tanzanian regulations and industry best practices
              for safety, data handling, and ethical employment.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {[
              { v: '10+',   l: 'Years in Business',     Icon: FiTrendingUp },
              { v: '500+',  l: 'Happy Clients',          Icon: BsPeopleFill },
              { v: '1,000+', l: 'Personnel Deployed',    Icon: FiShield },
              { v: '2',     l: 'Offices in Tanzania',    Icon: FiTarget },
            ].map(s => (
              <div key={s.l} className="bg-navy-card rounded-xl p-6 text-center transition-all group">
                <s.Icon className="text-gold/50 mx-auto mb-2 group-hover:text-gold transition-colors" size={20} />
                <div className="font-display text-4xl font-extrabold text-gold mb-1">{s.v}</div>
                <div className="text-gray-400 text-sm">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── VISION & MISSION ── */}
      <section className="py-20 bg-navy-mid">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="section-tag">Our Foundation</span>
            <h2 className="section-title">
              Vision, Mission <span className="text-gold">&amp; Values</span>
            </h2>
            <div className="gold-divider mx-auto" />
          </div>
          <div className="grid md:grid-cols-2 gap-8 mb-10">
            <div className="bg-navy-card rounded-2xl p-8 transition-all">
              <div className="w-12 h-12 rounded-lg bg-gold/10 flex items-center justify-center mb-5">
                <FiEye className="text-gold" size={22} />
              </div>
              <h3 className="text-white font-display text-2xl font-bold mb-3">Our Vision</h3>
              <div className="gold-divider" />
              <p className="text-gray-400 leading-relaxed">
                To be Tanzania's most trusted provider of integrated security and workforce solutions — setting
                the standard for professionalism and reliability across East Africa.
              </p>
            </div>
            <div className="bg-navy-card rounded-2xl p-8 transition-all">
              <div className="w-12 h-12 rounded-lg bg-gold/10 flex items-center justify-center mb-5">
                <FiTarget className="text-gold" size={22} />
              </div>
              <h3 className="text-white font-display text-2xl font-bold mb-3">Our Mission</h3>
              <div className="gold-divider" />
              <p className="text-gray-400 leading-relaxed">
                Deliver top-quality guarding, cutting-edge security technologies, and agile manpower services that
                safeguard lives, assets, and business continuity for every client we serve.
              </p>
            </div>
          </div>

          {/* Values */}
          <div className="bg-navy-card rounded-2xl p-8">
            <h3 className="text-white font-semibold text-lg mb-6 text-center">Our Core Values</h3>
            <div className="flex flex-wrap justify-center gap-4">
              {values.map(({ Icon, label }) => (
                <div key={label} className="flex items-center gap-2 bg-gold/5 rounded-full px-5 py-2.5 hover:bg-gold/10 transition-colors cursor-default">
                  <Icon className="text-gold" size={15} />
                  <span className="text-white font-medium text-sm">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── TIMELINE ── */}
      <section className="py-20 max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          <span className="section-tag">Our Journey</span>
          <h2 className="section-title">Company <span className="text-gold">Milestones</span></h2>
          <div className="gold-divider mx-auto" />
        </div>
        <div className="relative max-w-3xl mx-auto">
          <div className="absolute left-6 top-0 bottom-0 w-px bg-gradient-to-b from-gold/60 via-gold/20 to-transparent" />
          <div className="space-y-8">
            {milestones.map((m, i) => (
              <div key={i} className="relative flex gap-6 items-start pl-16">
                <div className="absolute left-0 w-12 h-12 rounded-full bg-gradient-to-br from-gold to-gold-light flex items-center justify-center text-navy-DEFAULT font-bold text-xs z-10 shadow-[0_0_16px_rgba(201,168,76,0.4)]">
                  {m.year}
                </div>
                <div className="bg-navy-card rounded-xl p-5 flex-1 transition-all">
                  <p className="text-gray-300 text-sm leading-relaxed">{m.event}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TEAM ── */}
      <section className="py-20 bg-navy-mid">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="section-tag">Our Team</span>
            <h2 className="section-title">Meet the <span className="text-gold">Leadership</span></h2>
            <div className="gold-divider mx-auto" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {team.map(t => (
              <div key={t.name} className="bg-navy-card rounded-2xl p-6 text-center hover:-translate-y-1 transition-all duration-300 group">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-navy-light to-navy-mid flex items-center justify-center font-display text-2xl font-bold text-gold mx-auto mb-4 transition-all shadow-[0_0_20px_rgba(201,168,76,0.15)]">
                  {t.initials}
                </div>
                <h4 className="text-white font-semibold mb-1">{t.name}</h4>
                <p className="text-gold text-xs font-medium tracking-wide uppercase">{t.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── COMPLIANCE BADGES ── */}
      <section className="py-16 max-w-7xl mx-auto px-6">
        <div className="text-center mb-10">
          <span className="section-tag">Compliance</span>
          <h2 className="section-title mt-2">Licensed, Insured <span className="text-gold">&amp; Compliant</span></h2>
          <div className="gold-divider mx-auto" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { Icon: MdOutlineVerifiedUser, label: 'Fully Licensed' },
            { Icon: FiShield,             label: 'Tanzanian Compliant' },
            { Icon: FiAward,              label: 'ISO-aligned Practices' },
            { Icon: FiStar,               label: 'Quality Certified' },
          ].map(({ Icon, label }) => (
            <div key={label} className="bg-navy-card rounded-xl p-5 flex flex-col items-center gap-3 transition-all">
              <Icon className="text-gold" size={28} />
              <span className="text-gray-300 text-sm font-medium text-center">{label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── FEEDBACK SECTION ── */}
      <section className="py-20 bg-navy-mid">
        <div className="max-w-7xl mx-auto px-6">
          <div className="relative rounded-2xl overflow-hidden bg-navy-card p-10 md:p-14">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(201,168,76,0.06),transparent_60%)]" />
            <div className="relative grid lg:grid-cols-5 gap-10 items-center">
              <div className="lg:col-span-3">
                <span className="section-tag">Client Feedback</span>
                <h2 className="section-title mb-4">We Value <span className="text-gold">Your Feedback</span></h2>
                <div className="gold-divider" />
                <p className="text-gray-400 leading-relaxed mb-4">
                  At KIKI SECURITY (T) LTD, we believe that client feedback is the cornerstone of our growth and excellence.
                  Your insights help us refine our services and ensure the most effective solutions tailored to your needs.
                </p>
                <p className="text-gray-400 leading-relaxed">
                  We encourage open communication and appreciate both positive reviews and constructive criticism.
                  By fostering a culture of continuous improvement, we reinforce our commitment to delivering reliable,
                  efficient, and innovative security solutions.
                </p>
              </div>
              <div className="lg:col-span-2 flex flex-col gap-4">
                <div className="bg-navy-mid rounded-xl p-5">
                  <FiMessageCircle className="text-gold mb-3" size={22} />
                  <p className="text-gray-300 text-sm italic leading-relaxed">
                    "Reliable, professional, and always responsive. KIKI Security has been our trusted partner for over 3 years."
                  </p>
                  <p className="text-gold text-xs font-medium mt-3">— Corporate Client, Dar es Salaam</p>
                </div>
                <Link to="/contact" className="btn-primary justify-center">
                  Share Your Feedback <FiArrowRight size={15} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
